/*
 * Image classification — DeckorateAPI client (AIVA-5376).
 *
 * `POST /v1/deckorate/image/classify` takes a blob reference and answers what
 * the picture *contains*: a chart, a table, a portrait, a sketch, or none of
 * those. Office.js only reports "this shape is a picture", so this is the only
 * way the chip dock can offer the content-specific actions from the mockup.
 *
 * The endpoint reads the image from Azure Blob Storage rather than accepting
 * bytes, so a caller must upload first — see `classifySelectedImage` in
 * `classifySelection.ts`, which chains blob-sas → PUT → classify.
 *
 * Field naming mirrors the FastAPI models exactly: the wire envelope is
 * snake_case (`request_id`, `image_type`, `banner_label`).
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import { logError, scrubMessage } from "../../../../../telemetry";
import { newRequestId } from "../../shared/services/requestId";
import { devTrace } from "../../shared/services/devTrace";
import type { ClassifiedImageKind } from "../types";

/** connectorSystemName tag for all image-classification telemetry. */
const CLASSIFY_CONNECTOR = "deckorate";

/** The DeckorateAPI route this client drives. */
const CLASSIFY_PATH = "/v1/deckorate/image/classify";

/** Prefix for this client's correlation ids. */
const REQUEST_ID_PREFIX = "classify-image-";

/**
 * How long to wait for a vision pass before giving up. A classification that
 * never returns must not leave the dock waiting: on timeout the picture keeps
 * the generic `image` chip set, which is a usable offer on its own.
 */
export const CLASSIFY_REQUEST_TIMEOUT_MS = 30_000;

/** Centralized so every catch site in this feature extracts messages the same way. */
export function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/** Scope tag for this chain's dev traces. */
export const CLASSIFY_TRACE_SCOPE = "classify";

/**
 * Reports a classification failure through the shared telemetry pipeline.
 *
 * Also mirrored to a dev-only trace: the telemetry endpoint is unset in local
 * dev, so without this a broken step in the chain reaches nobody at all. The
 * mirror is compiled out of production builds, so the "never `console.*`" rule
 * still holds for a shipped bundle.
 */
export function logClassifyError(reason: string, message: string): void {
  logError(
    "error.connector.failure",
    { type: "ImageClassifyError", scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: CLASSIFY_CONNECTOR, reason },
  );
  devTrace(CLASSIFY_TRACE_SCOPE, `FAILED: ${reason}`, { message: scrubMessage(message) });
}

/** Wire request. `blob_container` is omitted to let the API use its default. */
interface ClassifyImageApiRequest {
  request_id: string;
  blob_name: string;
  blob_container?: string;
}

/**
 * Wire response. `actions` is deliberately not modelled: the chip set is
 * resolved locally from `image_type` (see the note on SELECTION_CHIPS), so
 * reading it here would create a second registry with no consumer.
 */
interface ClassifyImageApiResponse {
  request_id: string;
  image_type: string;
  confidence: number;
  source: string;
  banner_label: string;
}

/** What the dock needs from one classification. */
export interface ImageClassification {
  readonly kind: ClassifiedImageKind;
  /** Ready-to-render context-banner text, straight from the API. */
  readonly bannerLabel: string;
  /** True when the type came from the vision model rather than the fallback. */
  readonly fromVision: boolean;
}

/**
 * `image_type` → the dock's selection kind.
 *
 * Written as a lookup rather than a switch so an unrecognised value (a type
 * added to the API before this client knows about it) resolves to `null` and is
 * handled as "not classified" instead of throwing or silently reading as one of
 * the known kinds.
 */
const KIND_BY_IMAGE_TYPE: Readonly<Record<string, ClassifiedImageKind>> = {
  chart: "imageChart",
  table: "imageTable",
  portrait: "imagePortrait",
  sketch: "imageSketch",
  general: "imageGeneral",
};

/**
 * Maps a wire `image_type` to a selection kind, or `null` if the API sent one
 * this build doesn't know.
 *
 * Checked for being a string rather than merely present: the response is parsed
 * JSON, so a key like `"constructor"` would otherwise resolve to an inherited
 * function from `Object.prototype` and read as a valid kind.
 */
export function kindOfImageType(imageType: string): ClassifiedImageKind | null {
  const kind = KIND_BY_IMAGE_TYPE[imageType];
  return typeof kind === "string" ? kind : null;
}

/**
 * POST /v1/deckorate/image/classify.
 *
 * Resolves to `null` — rather than rejecting — when the API answers with a type
 * this build doesn't recognise. Every other failure (transport, non-2xx,
 * malformed body) rejects, so the caller can report it once and fall back to the
 * generic image chips.
 */
export async function postClassifyImage(
  http: HttpClient,
  blob: { readonly blobName: string; readonly blobContainer?: string },
  options: HttpRequestOptions = {},
): Promise<ImageClassification | null> {
  const request: ClassifyImageApiRequest = {
    request_id: newRequestId(REQUEST_ID_PREFIX),
    blob_name: blob.blobName,
    ...(blob.blobContainer ? { blob_container: blob.blobContainer } : {}),
  };

  devTrace(CLASSIFY_TRACE_SCOPE, "POST image/classify", {
    path: CLASSIFY_PATH,
    blobName: blob.blobName,
    blobContainer: blob.blobContainer ?? "(api default)",
  });

  const response = await http.post<ClassifyImageApiResponse>(CLASSIFY_PATH, request, options);

  devTrace(CLASSIFY_TRACE_SCOPE, "classify responded", {
    imageType: response?.image_type,
    confidence: response?.confidence,
    source: response?.source,
  });

  // A 2xx with a body missing image_type is a contract break, not a classification.
  // TypeError rather than Error: the failed check is a type check, so the
  // specific built-in is the one that describes it (Sonar typescript:S7786).
  if (typeof response?.image_type !== "string") {
    throw new TypeError("The classify response did not include an image type.");
  }

  const kind = kindOfImageType(response.image_type);
  if (!kind) {
    logClassifyError("classify-unknown-image-type", `Unrecognised image_type: ${response.image_type}`);
    return null;
  }

  return {
    kind,
    // The API's label is preferred, but an empty or absent one must not blank
    // the banner — the caller falls back to the local label when this is "".
    bannerLabel: typeof response.banner_label === "string" ? response.banner_label : "",
    fromVision: response.source === "ai_vision",
  };
}
